import React from 'react';
import { Globe, Loader2, Search } from 'lucide-react';
import { MAX_SCAN_DELAY_MS, clampScanDelayMs } from '../../utils/analyzeWebsiteHelpers';

// URL input + scan button and the model / resize / compression / delay / RAG
// settings block for the "Analyse web-site" page.
const AnalyzeWebsiteControls = ({
  websiteUrl,
  setWebsiteUrl,
  loadingScan,
  onScan,
  modelName,
  setModelName,
  resizeMode,
  setResizeMode,
  compressionMode,
  setCompressionMode,
  scanDelayMs,
  setScanDelayMs,
  useEbayRag,
  setUseEbayRag,
  useNotesRag,
  setUseNotesRag,
  assumeHiddenItems,
  setAssumeHiddenItems,
}) => (
  <div className="mb-4 bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200 space-y-3">
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2">
      <span className="text-sm font-medium text-slate-700 shrink-0">URL:</span>
      <div className="relative flex-1">
        <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://example.com/listing"
          className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        />
      </div>
      <button
        type="button"
        onClick={onScan}
        disabled={loadingScan || !websiteUrl.trim()}
        className={`px-3 py-1.5 rounded text-sm font-medium text-white ${loadingScan || !websiteUrl.trim() ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
        title="Scan website"
      >
        {loadingScan
          ? <span className="inline-flex items-center gap-1"><Loader2 size={14} className="animate-spin" />Scanning</span>
          : <span className="inline-flex items-center gap-1"><Search size={14} />Scan</span>
        }
      </button>
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 text-sm">
      <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
        <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Model:</span>
        <select value={modelName} onChange={(e) => setModelName(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
          <optgroup label="GPT-5 Series (Latest)">
            <option value="gpt-5.4">gpt-5.4</option>
            <option value="gpt-5.4-mini">gpt-5.4-mini</option>
            <option value="gpt-5.2">gpt-5.2</option>
            <option value="gpt-5.1">gpt-5.1</option>
            <option value="gpt-5">gpt-5</option>
          </optgroup>
          <optgroup label="Gemini">
            <option value="gemini-flash-latest">gemini-flash-latest</option>
            <option value="gemini-2.5-pro">gemini-2.5-pro</option>
            <option value="gemini-2.5-flash">gemini-2.5-flash</option>
            <option value="gemini-2.0-flash">gemini-2.0-flash</option>
          </optgroup>
          <optgroup label="Claude">
            <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
            <option value="claude-haiku-4-5">claude-haiku-4-5</option>
            <option value="claude-opus-4-6">claude-opus-4-6</option>
            <option value="claude-opus-4-7">claude-opus-4-7</option>
          </optgroup>
        </select>
      </div>
      <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
        <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Resize:</span>
        <select value={resizeMode} onChange={(e) => setResizeMode(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
          <option value="original">Original</option>
          <option value="1600">Max 1600px</option>
          <option value="1280">Max 1280px</option>
          <option value="1024">Max 1024px</option>
          <option value="768">Max 768px</option>
          <option value="512">Max 512px</option>
          <option value="128">Max 128px</option>
        </select>
      </div>
      <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
        <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Compression:</span>
        <select value={compressionMode} onChange={(e) => setCompressionMode(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
          <option value="none">None</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>
      <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
        <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Load Pause (ms):</span>
        <input
          type="number"
          min="0"
          max={MAX_SCAN_DELAY_MS}
          step="100"
          value={scanDelayMs}
          onChange={(e) => {
            const next = Number(e.target.value);
            setScanDelayMs(Number.isFinite(next) ? clampScanDelayMs(next) : 0);
          }}
          className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium"
        />
      </div>
    </div>

    <div className="flex flex-wrap gap-3 text-sm">
      <label className="inline-flex items-center gap-2">
        <input type="checkbox" checked={useEbayRag} onChange={(e) => setUseEbayRag(e.target.checked)} />
        <span>eBay RAG</span>
      </label>
      <label className="inline-flex items-center gap-2">
        <input type="checkbox" checked={useNotesRag} onChange={(e) => setUseNotesRag(e.target.checked)} />
        <span>Notes RAG</span>
      </label>
      <label className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={assumeHiddenItems}
          onChange={(e) => setAssumeHiddenItems(e.target.checked)}
        />
        <span>Hidden Items</span>
      </label>
    </div>
  </div>
);

export default AnalyzeWebsiteControls;
